// Composition root: measures, packs and renders the Axis, Blocks and
// connectors, then wires pan, zoom and the navbar onto them.
import { markReady } from "./boot.js";
import type { Timeline } from "./content/timeline.js";
import { parseDate } from "./content/event.js";
import type { Event } from "./content/event.js";
import type { CategoryDeleteChoice } from "./content/categories.js";
import { isUnreadableEvent, describeUnreadable } from "./content/validate.js";
import type { StoredEvent, ValidationReport } from "./content/validate.js";
import { createTimelineSession } from "./content/session.js";
import type { Repaint } from "./content/session.js";
import type { Range } from "./content/range.js";
import { exportTimelines } from "./content/transfer.js";
import { attachFlushOnHide } from "./content/store.js";
import { createLibrary } from "./app/library.js";
import type { Outcome } from "./app/library.js";
import { buildScale } from "./layout/scale.js";
import type { Scale } from "./layout/scale.js";
import { loadDisplayFont, verifyFontStylesheet, canvasTextMeasurer } from "./layout/measure.js";
import { themeFontChoices } from "./content/fonts.js";
import { ensureFontLoaded } from "./render/fontload.js";
import { renderAxis } from "./render/axis.js";
import { applyThemeColours } from "./render/theme.js";
import type { TickEvent } from "./render/axis.js";
import { openEventPopover, closeEventPopover } from "./render/editor.js";
import type { EventFormData } from "./render/editor.js";
import { describeStoreStatus, describeSaveFailure, describeUnreadableTimelines } from "./render/messages.js";
import { showReportBanner, showStoreStatusBanner, showErrorBanner, showImportBanner } from "./render/banners.js";
import { blockPopoverAnchor } from "./layout/popover.js";
import { createSizeCache } from "./layout/sizes.js";
import {
  layoutBlocksFull,
  renderBlocks,
  captureBlocks,
  settleBlocks,
  wireBlockInteraction,
  computeRowMetrics,
} from "./render/blocks.js";
import type { BlockEvent, LaidOutBlock, RowMetrics } from "./render/blocks.js";
import { renderConnectors } from "./render/connectors.js";
import type { ConnectorEvent } from "./render/connectors.js";
import { attachPan, renderPanSlider, panXFromThumbFraction } from "./render/pan.js";
import { attachZoom } from "./render/zoom.js";
import { renderNavbar } from "./render/navbar.js";
import type { TimelineOption } from "./render/navbar.js";

markReady(document);

type Renderable = {
  event: Event;
  id: string;
  displayYear: string;
  astronomicalYear: number;
};

/**
 * Never throws: an Unreadable Event is set aside for the banner rather than
 * blanking the whole page over one typo. builtinTimeline.events is already
 * strict-validated (timeline.ts), so in practice this never finds one for
 * the built-in -- it exists so the render path needs no change once a
 * Timeline can come from storage or an import (#68, #69), which do carry
 * them.
 */
function parseEvents(source: readonly StoredEvent[]): { ok: Renderable[]; report: ValidationReport } {
  const ok: Renderable[] = [];
  const unreadable: ValidationReport["unreadable"][number][] = [];

  for (const event of source) {
    if (isUnreadableEvent(event)) {
      unreadable.push(describeUnreadable(event));
      continue;
    }
    // A validated Event's date is expected to parse -- but this is the
    // render path's own crash guard, not a trust exercise: a Timeline
    // handed to it some other way (a future direct call, a bug upstream)
    // must degrade to "one more Event set aside," never a blank page.
    try {
      const parsed = parseDate(event.date);
      ok.push({
        event,
        id: event.id,
        displayYear: parsed.displayYear,
        astronomicalYear: parsed.astronomicalYear,
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      unreadable.push({ title: event.title, reason });
    }
  }

  ok.sort((a, b) => a.astronomicalYear - b.astronomicalYear);
  return { ok, report: { repairs: [], unreadable } };
}

/** What renderStage needs from the lifecycle above it: the store, the switcher and Export/Import all live one level up, in main.ts's own boot sequence, because they outlive any one Timeline's render. */
type StageHooks = {
  listTimelines(): TimelineOption[];
  onSwitchTimeline(id: string): void;
  onExport(): void;
  onImportFile(file: File): void;
  onNewTimeline(data: { name: string; range: Range }): void;
  onDuplicateTimeline(id: string): void;
  onRenameTimeline(id: string, name: string): void;
  onDeleteTimeline(id: string): void;
  /** Persists a Range renderStage() has already applied to the Timeline and the page -- no reload. */
  onRangeChanged(range: Range): void;
  /** Called after any Theme or Registry edit reaches `timeline` in place, so main.ts can schedule an autosave. A no-op for the built-in, which the navbar never offers editing on. */
  notifyEdited(): void;
};

/**
 * The Stage is the fixed viewport; the World is the full-length canvas that
 * pans inside it. Both, the two sliders and the Axis are created even with no
 * Events -- the Scale comes from the Timeline's declared Range, never from
 * `items`, so an empty Timeline still has an Axis and something to click.
 *
 * `timeline` is held by reference for the life of this render: the navbar
 * (#73) reassigns its `.theme`/`.registry` in place as the reader edits, and
 * every read below goes through `timeline.theme`/`timeline.registry` rather
 * than a value captured once, so an edit reaches the very next repaint with
 * no threading.
 */
async function renderStage(timeline: Timeline, items: Renderable[], hooks: StageHooks): Promise<void> {
  // Colours reach the page as CSS custom properties, once, as the Timeline
  // opens (#72), and again on every edit (refreshTheme() below). Families
  // reach it inline instead, at renderWorld()'s own renderAxis()/
  // renderBlocks() calls.
  applyThemeColours(timeline.theme);

  const stage = document.createElement("div");
  stage.id = "stage";

  const world = document.createElement("div");
  world.id = "world";
  stage.appendChild(world);
  document.body.appendChild(stage);

  // The navbar band: a real flex sibling immediately before #stage, in both
  // reading and editing mode, never an overlay (#73). Its return is kept (not
  // just the mounted element) so commitDeleteEvent/commitDeleteCategory/onUndo
  // below can refresh the Undo icon's enabled state after every push or pop
  // of the stack -- the only piece of this band anything outside its own
  // gestures ever needs to touch (#76).
  const nav = renderNavbar(document.body, stage, timeline, {
    listTimelines: hooks.listTimelines,
    onSwitchTimeline: hooks.onSwitchTimeline,
    onExport: hooks.onExport,
    onImportFile: hooks.onImportFile,
    onNewTimeline: hooks.onNewTimeline,
    onDuplicateTimeline: hooks.onDuplicateTimeline,
    onRenameTimeline: hooks.onRenameTimeline,
    onDeleteTimeline: hooks.onDeleteTimeline,
    onRangeChanged: (range) => commitRange(range),
    onThemeChanged: refreshTheme,
    onEditModeChanged: (editing) => {
      editingMode = editing;
      // Toggling the mode changes what a click on the Axis or a Block does
      // (#74); rewiring both is simplest as one more clean renderWorld(),
      // same accepted cost as a zoom or a Theme edit -- expand/collapse state
      // resets, and any open Event popover -- anchored to controls this mode
      // switch is about to repaint -- closes rather than dangling.
      renderWorld(currentScale);
    },
    onDeleteCategory: (key, choice) => commitDeleteCategory(key, choice),
    // session.undoDescription() is the one source of truth for both: null
    // means there is nothing to undo, a string means there is and names it.
    canUndo: () => session.undoDescription() !== null,
    undoDescription: () => session.undoDescription(),
    onUndo: () => commitUndo(),
  });

  // The zoom slider is fixed to the viewport but measured from the Stage's
  // own top (styles.css's .zoom-slider-shell), so a banner arriving above the
  // band later -- which pushes the Stage down and shrinks it by the same
  // amount -- moves the slider with it instead of under the Timeline's name.
  const syncStageTop = (): void => {
    document.documentElement.style.setProperty("--stage-top", `${stage.getBoundingClientRect().top}px`);
  };
  syncStageTop();
  new ResizeObserver(syncStageTop).observe(stage);

  const { start: minYear, end: maxYear } = timeline.range;
  const scale = buildScale(minYear, maxYear);
  world.style.width = `${scale.length}px`;

  // RowMetrics derives from the Timeline's Theme and the Stage's real height
  // so the mosaic fills the viewport at the requested density, or fewer rows
  // if the screen is too short to show that many legibly. Kept in sync by
  // the ResizeObserver further down, and by refreshTheme() when the reader
  // moves the Rows control -- both read `timeline.theme.rowsPerSide` fresh
  // rather than a value captured once, so an edit takes effect without
  // re-deriving anything else. computeRowMetrics() itself rounds to whole
  // px, so a Theme edit that touches nothing about layout (a colour, say)
  // can never shift the row height by the fraction of a px this render
  // started with -- all three call sites go through the one function.
  let metrics: RowMetrics = computeRowMetrics(stage.clientHeight, timeline.theme.rowsPerSide);

  // The Scale in force, updated via onZoom. attachPan() reads world length
  // through a closure, so it needs no Scale reference of its own.
  let currentScale = scale;

  // Edit mode (#74): true between the pencil going down and coming back up,
  // read fresh by renderWorld() below so the Axis and Blocks take click for
  // create/edit/delete exactly while it is. `axisEl` is the Axis renderWorld()
  // most recently built -- the Event popover's own anchor math needs its
  // live getBoundingClientRect(), so this is reassigned on every renderWorld()
  // call rather than captured once.
  let editingMode = false;
  let axisEl: HTMLElement | null = null;

  // The Timeline's own editing rules (#74/#76) -- Event create/edit/delete,
  // Category delete, undo and the Range's owner-edit path -- live in
  // content/session.ts now, exercised there without a DOM. `session.timeline`
  // is this same `timeline` reference, never a copy, so every mutation lands
  // exactly where the navbar (#73) and the autosave scheduler's own `entries`
  // already expect it. The Undo stack it owns is in memory only, held by this
  // one `const`: "cleared on reload" and "cleared on switching Timeline" need
  // no code of their own, since both already tear down this whole
  // renderStage() call (a fresh page load), taking `session` with them.
  const session = createTimelineSession(timeline);

  // Drag, keys and the slider share one PanController, so a Scale change
  // drives panX through the same clamp. The slider goes on the black
  // surround, not `stage`, so it does not pan away with the World. A track-jump
  // glides (smoothPanX); a thumb-drag stays 1:1 (setPanX).
  const slider = renderPanSlider(document.body, (fraction, jump) => {
    const target = panXFromThumbFraction(fraction, currentScale.length, stage.clientWidth);
    if (jump) pan.smoothPanX(target);
    else pan.setPanX(target);
  });
  const pan = attachPan(stage, world, () => currentScale.length, slider);

  // A `let`, not `const` (#74): an Event created, edited or deleted through
  // the popover changes this array's own membership and content, not just
  // `timeline.events` -- rebuildBlockEvents() re-derives it the same way this
  // first assignment does, and every commit function below calls it before
  // the renderWorld() that shows the result.
  let blockEvents: BlockEvent[] = toBlockEvents(items);

  function toBlockEvents(renderable: readonly Renderable[]): BlockEvent[] {
    return renderable.map(({ event, id, astronomicalYear }) => ({
      id,
      year: astronomicalYear,
      category: event.category,
      title: event.title,
      body: event.body,
      shorthand: event.shorthand,
    }));
  }

  /** Re-derives blockEvents from timeline.events, for after an Event is created, edited or deleted (#74). Reuses parseEvents() -- the same skip-and-warn path the initial render went through -- rather than trusting a freshly committed Event has stayed valid, on the same "never blank the page over one bad Event" principle as the rest of this file. */
  function rebuildBlockEvents(): void {
    blockEvents = toBlockEvents(parseEvents(timeline.events).ok);
  }

  /**
   * Clears the World first, so a zoom renders clean rather than over a stale
   * one. Expand/collapse state resets on each zoom (accepted).
   *
   * The measurer is reused: title widths are Scale-independent.
   */
  // Measured once and reused: a zoom tick changes only the Scale, which
  // textWidth() never reads, so re-measuring every Event per wheel tick bought
  // nothing and read as jank. The cache (layout/sizes.ts) notices a row-height,
  // font or title/body change by itself; it is invalidated by hand only below,
  // if a font arrives after measurement already ran.
  const sizeCache = createSizeCache();

  // Two independent signals (#71 research), run concurrently rather than one
  // gating the other: loadDisplayFont() is authoritative -- its own
  // non-empty check is what the page's actual rendering depends on -- while
  // verifyFontStylesheet() is a diagnostic only, logged but never vetoing a
  // font that did register. A fetch() blocked by some extension that still
  // lets the page's own <link rel="stylesheet"> through must not report a
  // working font as failed.
  const fontLoad: Promise<void> = Promise.all([
    verifyFontStylesheet().catch((err) => {
      console.error("Title font stylesheet check failed (diagnostic only; load() below is authoritative):", err);
    }),
    loadDisplayFont(document.fonts),
  ]).then(() => undefined);

  let titleFontReady = false;
  const fontSettled = fontLoad.then(
    () => {
      titleFontReady = true;
    },
    (err) => {
      // A webfont failing outright must not blank the page.
      console.error("Familjen Grotesk failed to load; measuring against the fallback face:", err);
    }
  );

  // The Theme's own curated families, if it paints in any: the font picker
  // linked them when they were chosen, but every Timeline open is a fresh
  // page load, so nothing else ever requests them again. ensureFontLoaded()
  // never rejects -- a failed family degrades to its generic.
  const themeFonts = themeFontChoices(timeline.theme);
  let themeFontsReady = themeFonts.length === 0;
  const themeFontsSettled = Promise.all(themeFonts.map(ensureFontLoaded)).then(() => {
    themeFontsReady = true;
  });

  // Measurement waits for the fonts, or every Text rectangle is sized against
  // the fallback face. Raced against a timeout so a stalled request can never
  // leave the page with nothing appearing at all.
  await Promise.race([Promise.all([fontSettled, themeFontsSettled]), timeout(FONT_LOAD_TIMEOUT_MS)]);
  const measuredAgainstFallback = !titleFontReady || !themeFontsReady;

  // A function declaration, not a const arrow: it is hoisted, so refreshTheme()
  // below and renderNavbar()'s onThemeChanged above can both close over it
  // regardless of where in this async function they happen to run first --
  // neither is ever called before renderStage() finishes setting up.
  function renderWorld(renderScale: Scale, protectedIds: ReadonlySet<string> = new Set(), settle = false): void {
    // Any open Event popover is anchored to DOM this render is about to
    // replace (the Axis, a Block) -- close it rather than leave it dangling
    // over controls that no longer exist (#74).
    closeEventPopover();

    const measurer = canvasTextMeasurer();
    const sizes = sizeCache.widths(blockEvents, metrics, timeline.theme, measurer);

    // A Tick's side comes from its Block's packed row, so packing runs once
    // here and feeds both renderers. If it fails outright, Ticks fall back to
    // alternating sides and Blocks simply don't render. `protectedIds` keeps
    // an Event just created or edited from being chosen as the collapse
    // victim of its own commit (#74).
    let packed: LaidOutBlock[] | null = null;
    try {
      packed = layoutBlocksFull(renderScale, blockEvents, measurer, { metrics, theme: timeline.theme, protectedIds, sizes });
    } catch (err) {
      console.error("Measuring/packing Blocks failed; Ticks fall back to alternating sides:", err);
    }
    // Built once and handed to both consumers rather than derived twice.
    const packedById = packed ? new Map(packed.map((item) => [item.id, item])) : null;

    const tickEvents: TickEvent[] = blockEvents.map((event, index) => ({
      id: event.id,
      year: event.year,
      category: event.category,
      // Alternating sides if an id is somehow absent from a successful pack.
      row: packedById?.get(event.id)?.row ?? index % 2,
    }));

    // world.style.width is the caller's job. `settle` (an Event mutation on
    // an unchanged Scale, afterEventMutation() below) keeps hold of the Blocks
    // about to be replaced, so the new ones can animate from where they stood.
    const previousBlocks = settle ? captureBlocks(world) : null;
    world.replaceChildren();
    // Edit mode's Axis half (#74): click creates, at the year under the
    // pointer -- scale.toYear(), the exact inverse of the positioning math --
    // rounded to the whole year every Event's date actually is. Omitted
    // entirely in reading mode (and, since editingMode can only ever be true
    // once the pencil exists, never on the built-in either).
    axisEl = renderAxis(
      world,
      renderScale,
      tickEvents,
      timeline.theme,
      timeline.registry,
      editingMode ? (rawYear) => openCreatePopoverAt(Math.round(rawYear)) : undefined
    );

    if (packedById) {
      try {
        const blockEls = renderBlocks(world, blockEvents, packedById, metrics, timeline.theme, timeline.registry);
        if (previousBlocks) settleBlocks(world, previousBlocks, blockEls);
        // Connectors need the packed layout too, so they render only where
        // Blocks did. Reused by onRelayout below on every click, not just here.
        const connectorEvents = (byId: ReadonlyMap<string, LaidOutBlock>): ConnectorEvent[] =>
          blockEvents.map((event) => {
            const layout = byId.get(event.id)!;
            return {
              id: event.id,
              year: event.year,
              category: event.category,
              row: layout.row,
              anchorX: layout.pos + layout.size / 2,
            };
          });
        const connectors = renderConnectors(world, renderScale, connectorEvents(packedById), blockEls, metrics.rowHeight, timeline.registry);

        // Anchored and Slack freeze row membership, so a Tick can never change
        // side or depth and renderAxis() need not re-run here. Reflow can
        // reassign rows and would leave a stale Tick until reload -- accepted,
        // since Reflow is neither the default nor wired to any control.
        wireBlockInteraction({
          scale: renderScale,
          events: blockEvents,
          measureText: measurer,
          blocks: blockEls,
          initialLaid: packed!,
          precomputedSizes: sizes,
          metrics,
          theme: timeline.theme,
          registry: timeline.registry,
          onRelayout: (byId) => connectors.update(renderScale, connectorEvents(byId)),
          editing: editingMode,
          onEditOpen: (id, layout) => openEditPopoverFor(id, layout),
          onEditDelete: (id) => commitDeleteEvent(id),
        });

      } catch (err) {
        // One rendering failure must not blank the page; the Axis is up already.
        console.error("Blocks failed to render:", err);
      }
    }
  }

  // ---------------------------------------------------------------------
  // Event create/edit/delete, Category delete, undo and Range (#74/#76): the
  // rules themselves live in `session` now (content/session.ts) -- what's left
  // here is DOM glue, turning a session.ts Repaint into an actual repaint
  // (applyRepaint()) and calling nav.refreshUndo() after whichever of these
  // touch the Undo stack. No separate undo for an ordinary Event edit: nothing
  // reaches `timeline` until Save, so Cancel is simply closing the popover
  // with no commit at all.
  // ---------------------------------------------------------------------

  /**
   * Turns a session.ts Repaint into the actual repaint: rebuilds blockEvents,
   * repaints with `protectId` exempted from its own commit's collapse search,
   * and schedules the autosave.
   *
   * `rangeWidened` matters because a Scale's minYear/maxYear are otherwise
   * fixed for the life of this render (attachZoom() closes over them once) --
   * an Event landing outside the Range is new territory for a live session
   * (every earlier widen happened at load, before pan/zoom existed to go
   * stale), so this rebuilds `currentScale` and the zoom controller's own
   * bounds through zoom.updateRange() and re-clamps panX, exactly the work a
   * fresh page load would have done with the wider Range from the start.
   *
   * Every such repaint settles (render/blocks.ts's settleBlocks()) rather
   * than snapping: the Blocks around a deleted Event grow into its space, a
   * restored or created one fades in while its neighbours make room.
   */
  function applyRepaint({ protectId, rangeWidened }: Repaint): void {
    rebuildBlockEvents();
    const panX = rangeWidened ? rescaleToRange() : null;
    // Animated only on the same Scale: a widened Range moves every Block's
    // own position along with it, which reads as the whole World lurching
    // rather than the neighbours closing up.
    renderWorld(currentScale, protectId ? new Set([protectId]) : undefined, !rangeWidened);
    if (panX !== null) pan.setPanX(panX);
    if (rangeWidened) nav.refreshRange();
    hooks.notifyEdited();
  }

  /**
   * Rebuilds `currentScale` and the zoom controller's own bounds for
   * `timeline.range` as it now stands, keeping the Scale the reader chose
   * (re-clamped to the new fit-to-viewport floor, same as a resize) -- and
   * returns the panX that keeps the year at the middle of the Stage where it
   * was. Every main-axis position moves when the Range's start does, so the
   * old panX alone would land the view on some other stretch of history. The
   * caller renders, then hands the result to pan.setPanX(), which clamps it.
   */
  function rescaleToRange(): number {
    const viewport = stage.clientWidth;
    const centreYear = currentScale.toYear(-pan.getPanX() + viewport / 2);
    currentScale = zoom.updateRange(timeline.range.start, timeline.range.end, viewport).scale;
    return viewport / 2 - currentScale.toMainAxis(centreYear);
  }

  /**
   * The Range controls next to the name (#75): session.setRange() already
   * applies the owner's edit to `timeline` in place (widened if needed, never
   * narrower than the Events already on it -- content/session.ts's own doc).
   * A `changed` Range rebuilds the Scale, zoom bounds and pan clamp live
   * through rescaleToRange(), so edit mode, the Scale and the stretch of
   * history on screen all survive the change -- a reload would reset every
   * one of them. Returns the Range actually applied, so the controls can
   * show it.
   */
  function commitRange(requested: Range): Range {
    const { applied, changed } = session.setRange(requested);
    if (!changed) return applied;
    const panX = rescaleToRange();
    renderWorld(currentScale);
    pan.setPanX(panX);
    hooks.onRangeChanged(applied);
    return applied;
  }

  function commitNewEvent(data: EventFormData): void {
    applyRepaint(session.createEvent(data));
  }

  function commitEditEvent(id: string, data: EventFormData): void {
    const repaint = session.editEvent(id, data);
    if (repaint) applyRepaint(repaint);
  }

  function commitDeleteEvent(id: string): void {
    const repaint = session.deleteEvent(id);
    if (!repaint) return; // already gone under this popover; nothing to delete or push
    applyRepaint(repaint);
    nav.refreshUndo();
  }

  function commitDeleteCategory(key: string, choice: CategoryDeleteChoice): void {
    const repaint = session.deleteCategory(key, choice);
    if (!repaint) return; // deleted already under a stale popover/dropdown
    applyRepaint(repaint);
    nav.refreshUndo();
  }

  /** Reverses the most recent destructive act via session.undo(), then repaints -- a no-op if the stack is empty (#76). */
  function commitUndo(): void {
    const repaint = session.undo();
    if (!repaint) return;
    applyRepaint(repaint);
    nav.refreshUndo();
  }

  /** The Axis half of edit mode: anchors the popover to the Axis itself, peak on the Tick the new Event will actually get (#74's own spec) -- there is no Block yet to point at. `year` is already rounded by the caller. */
  function openCreatePopoverAt(year: number): void {
    if (!axisEl) return;
    const axisRect = axisEl.getBoundingClientRect();
    openEventPopover({
      mode: "create",
      initial: { date: year },
      registry: timeline.registry,
      anchor: { x: axisRect.left + currentScale.toMainAxis(year), top: axisRect.top, bottom: axisRect.bottom },
      onSave: commitNewEvent,
      onCancel: () => {},
    });
  }

  /**
   * The Block half: the peak points at the Block itself, the way the navbar
   * prototype had it. blockPopoverAnchor() (layout/popover.ts) owns the pure
   * placement math -- this just supplies the live Axis rect and the render's
   * own row height.
   */
  function openEditPopoverFor(id: string, layout: LaidOutBlock): void {
    if (!axisEl) return;
    const event = timeline.events.find((e): e is Event => !isUnreadableEvent(e) && e.id === id);
    if (!event) return;
    const axisRect = axisEl.getBoundingClientRect();
    const anchor = blockPopoverAnchor(axisRect, layout, metrics.rowHeight, window.innerWidth);
    openEventPopover({
      mode: "edit",
      initial: { title: event.title, shorthand: event.shorthand, body: event.body, date: event.date, category: event.category },
      registry: timeline.registry,
      anchor,
      onSave: (data) => commitEditEvent(id, data),
      onDelete: () => commitDeleteEvent(id),
      onCancel: () => {},
    });
  }

  /**
   * The navbar's onThemeChanged hook (#73): a Theme or Registry edit already
   * reached `timeline` in place by the time this runs. Colours are cheap
   * (custom properties only); a font change re-measures every Block, which
   * the size cache notices by itself; rowsPerSide needs the same
   * effective-count clamp a resize gets. All three end the same way: one
   * clean renderWorld(), same as a zoom tick or a resize -- expand/collapse
   * state resets, the same accepted cost as those.
   */
  function refreshTheme(): void {
    applyThemeColours(timeline.theme);
    metrics = computeRowMetrics(stage.clientHeight, timeline.theme.rowsPerSide);
    renderWorld(currentScale);
    // B2: a Theme or Registry edit reaches `timeline.theme`/`.registry` in
    // place before this hook ever runs (render/navbar.ts's own editors), same
    // as an Event edit does through content/session.ts -- so this stamps
    // editedAt for exactly the same reason session.ts's stampEdited() does,
    // rather than leaving it lagging behind every edit that isn't a rename or
    // a Range change.
    timeline.editedAt = new Date().toISOString();
    hooks.notifyEdited();
  }

  renderWorld(scale);

  // The timeout won the race above, so the render just above measured every
  // Text rectangle against the fallback face. If a font is merely slow
  // rather than actually failed, it still finishes loading in the
  // background; this re-measures once it does, rather than leaving the
  // mosaic permanently packed against system-ui (the "no re-measure on late
  // arrival" gap #71's research flagged). A genuine title-font failure with
  // no Theme fonts to wait on resolves titleFontReady false forever, so this
  // simply never fires.
  //
  // Same accepted cost as a zoom's clean re-render above: expand/collapse
  // state resets. Rarer and narrower here -- only the first FONT_LOAD_TIMEOUT_MS
  // of a page visit, on a connection slow enough to miss that window at all --
  // and the alternative is the page staying wrong about every title's face
  // for the rest of the session.
  if (measuredAgainstFallback) {
    void Promise.all([fontSettled, themeFontsSettled]).then(() => {
      if (!titleFontReady && themeFonts.length === 0) return;
      sizeCache.invalidate(); // the cached widths above were measured against system-ui, not the real face
      renderWorld(currentScale);
    });
  }

  // Wired before the landing state below, because attachZoom() may clamp its
  // starting Scale up to the fit-to-viewport floor. When it does, the World was
  // just painted one Scale too low, and everything after this must work from
  // the Scale actually in force.
  const zoom = attachZoom(stage, world, minYear, maxYear, document.body, pan, (newScale, newPanX) => {
    currentScale = newScale;
    renderWorld(newScale);
    // attachPan's closure now reads the updated length, so this re-clamps to
    // the new bounds and refreshes the thumb width in one step.
    pan.setPanX(newPanX);
  });
  if (zoom.getScale() !== currentScale.pxPerYear) {
    currentScale = buildScale(minYear, maxYear, zoom.getScale());
    world.style.width = `${currentScale.length}px`;
    renderWorld(currentScale);
  }

  // The landing state is the present-day end, pan at maximum. setPanX() clamps
  // internally, so -Infinity lands exactly there whatever the sizes.
  pan.setPanX(-Infinity);

  // Keeps `metrics` in sync on a live resize. Guarded on an actual change:
  // the raw row height drifts by fractions of a pixel on nearly every tick of
  // a drag-resize, and a strict `===` would thrash a full relayout
  // continuously instead of once the size settles -- computeRowMetrics()
  // already rounds to whole px, so this comparison alone catches that.
  // rowsPerSide never drifts this way -- it is an integer already -- but a
  // row-count change still needs the same guard so a resize that leaves it
  // untouched doesn't force a relayout on its own.
  //
  // Only Block and connector geometry depends on either, so world width and
  // panX need no refresh.
  new ResizeObserver(() => {
    const next = computeRowMetrics(stage.clientHeight, timeline.theme.rowsPerSide);
    if (next.rowsPerSide === metrics.rowsPerSide && next.rowHeight === metrics.rowHeight) return;
    metrics = next;
    renderWorld(currentScale);
  }).observe(stage);

  // Ctrl/Cmd+Z as a shortcut for the navbar's own Undo icon (#76) -- only
  // while editing (nothing to reverse otherwise reaches the stack) and never
  // while a text field has focus, so it stays out of the way of a reader
  // using their browser's native undo inside the Registry editor's name
  // input or the Event popover's own fields.
  document.addEventListener("keydown", (event) => {
    if (!editingMode) return;
    if (event.key.toLowerCase() !== "z" || !(event.ctrlKey || event.metaKey) || event.shiftKey) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    event.preventDefault();
    commitUndo(); // refreshes the Undo icon itself when it actually pops something
  });
}

/** How long to wait for the display font before measuring against the fallback face. */
const FONT_LOAD_TIMEOUT_MS = 3000;

function timeout(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Boot: open the library (app/library.ts -- the store, and which Timeline is
// open), then hand renderStage everything it needs to switch, edit, export
// and import from here on. Only the DOM half lives here: banners, the
// FileReader and the Blob download.
// ---------------------------------------------------------------------------

const library = createLibrary({
  storage: window.localStorage,
  session: window.sessionStorage,
  reload: () => location.reload(),
  // Otherwise a failed debounced save after a Theme/Registry edit is never
  // surfaced: the edit stays visible only in memory and is silently gone on
  // the next reload, with the reader never told.
  onAutosave: (result) => {
    if (result.ok) return;
    showErrorBanner(describeSaveFailure(null, result.reason));
  },
});
attachFlushOnHide(window, () => document.visibilityState === "hidden", library);

const storeStatusMessage = describeStoreStatus(library.loadResult);
if (storeStatusMessage !== null) {
  showStoreStatusBanner(storeStatusMessage);
}
const unreadableTimelinesMessage = describeUnreadableTimelines(library.unreadableTimelineCount);
if (unreadableTimelinesMessage !== null) {
  showStoreStatusBanner(unreadableTimelinesMessage);
}

const showOutcome = (outcome: Outcome | null): void => {
  if (outcome && !outcome.ok) showErrorBanner(outcome.message);
};

function onExport(): void {
  const json = exportTimelines([library.open]);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${library.open.name.replace(/[^a-z0-9]+/gi, "_").toLowerCase()}.timeline.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function onImportFile(file: File): void {
  // Checked before the file is even read, not only inside importText(): the
  // reader is told at once, rather than after a FileReader round-trip that
  // could never have landed.
  const blocked = library.writeBlockedMessage("Import");
  if (blocked !== null) {
    showErrorBanner(blocked);
    return;
  }
  const reader = new FileReader();
  reader.onerror = () => {
    showErrorBanner("The file could not be read. Nothing was imported -- try again.");
  };
  reader.onload = () => {
    const text = reader.result;
    if (typeof text !== "string") return;
    const outcome = library.importText(text);
    if ("message" in outcome) showErrorBanner(outcome.message);
    else showImportBanner(outcome);
  };
  reader.readAsText(file);
}

const { ok, report } = parseEvents(library.open.events);
// The repairs made while the store decoded this Timeline, plus whatever the
// render path's own guard set aside. Unreadable Events come from parseEvents()
// alone: the stored report names the very same ones.
showReportBanner({ repairs: [...library.openReport.repairs, ...report.repairs], unreadable: report.unreadable });
void renderStage(library.open, ok, {
  listTimelines: () => library.list(),
  onSwitchTimeline: (id) => library.switchTo(id),
  onExport,
  onImportFile,
  onNewTimeline: (data) => showOutcome(library.create(data.name, data.range)),
  onDuplicateTimeline: (id) => showOutcome(library.duplicate(id)),
  onRenameTimeline: (id, name) => library.rename(id, name),
  onDeleteTimeline: (id) => showOutcome(library.remove(id)),
  onRangeChanged: () => library.rangeEdited(),
  notifyEdited: () => library.edited(),
});
